import { useState, useCallback, useEffect } from 'react';
import { AccountService, PasswordResetResult, PasswordChangeResult } from '../services/AccountService';
import { validatePassword } from '../utils/accountValidation';

export interface UsePasswordResetReturn {
  requestReset: (email: string, captchaToken?: string) => Promise<void>;
  confirmReset: (newPassword: string, confirmPassword: string) => Promise<void>;
  validatePasswordStrength: (password: string) => { isValid: boolean; errors: string[] };
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  validationErrors: {
    email?: string;
    newPassword?: string;
    confirmPassword?: string;
  };
  clearError: () => void;
  clearSuccess: () => void;
  clearValidationErrors: () => void;
}

/**
 * Custom hook for password reset operations
 * Handles both requesting a password reset and confirming a new password
 */
export function usePasswordReset(): UsePasswordResetReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const validatePasswordStrength = useCallback((password: string) => {
    const validation = validatePassword(password);
    return {
      isValid: validation.isValid,
      errors: validation.isValid ? [] : [validation.error || 'Invalid password']
    };
  }, []);

  const validateEmail = (email: string): boolean => {
    if (!email || email.trim().length === 0) {
      setValidationErrors({ email: 'Email is required' });
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidationErrors({ email: 'Please enter a valid email address' });
      return false;
    }

    return true;
  };

  const requestReset = useCallback(async (email: string, captchaToken?: string) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    setValidationErrors({});

    try {
      // Validate email
      if (!validateEmail(email)) {
        throw new Error('Validation failed');
      }

      const result: PasswordResetResult = await AccountService.requestPasswordReset(email, captchaToken);

      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
        throw new Error(result.message);
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Validation failed') {
        const errorMessage = err.message || 'Failed to request password reset';
        setError(errorMessage);
      }
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const confirmReset = useCallback(async (
    newPassword: string,
    confirmPassword: string
  ) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    setValidationErrors({});

    try {
      // Validate inputs
      const errors: typeof validationErrors = {};

      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        errors.newPassword = passwordValidation.error || 'Invalid password';
      }

      if (newPassword !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        throw new Error('Validation failed');
      }

      const result: PasswordChangeResult = await AccountService.confirmPasswordReset(newPassword);

      if (result.success) {
        setSuccess(result.message || 'Password successfully reset');
      } else {
        setError(result.message);
        throw new Error(result.message);
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Validation failed') {
        const errorMessage = err.message || 'Failed to reset password';
        setError(errorMessage);
      }
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  return {
    requestReset,
    confirmReset,
    validatePasswordStrength,
    isSubmitting,
    error,
    success,
    validationErrors,
    clearError,
    clearSuccess,
    clearValidationErrors
  };
}
